import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../shared/services/baseQuery';
import { type EntitlementDetailsRowType } from '../../ViolationsEntitlement/data-layer';
import { useGetInherentRisksMutation } from '../components/EntitlementDetails/InherentRisksTab/data-layer/inherentRisksApi';
import { type UserViolationChartDetails } from '../components/EntitlementDetails/UsersTab/data-layer';
import { detailsActions } from '../data-store/detailsSlice';

import {
  DetailsType,
  type GetAssignedEntitlementApiResponse,
  type GetSodViolationsCountApiResponse,
  type GetViolatingEntitlementApiResponse,
  type GetViolationFunctionApiResponse,
  type GetViolationMitigatingControlApiResponse,
  type SodViolationsCountFilters,
  type ViolationConflictApisProp,
} from './detailsTypes';

export const detailsApi = createApi({
  reducerPath: DetailsType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  tagTypes: ['EntitlementDetails'],
  keepUnusedDataFor: 300, // 5 minutes cache
  endpoints: () => ({}),
});

// Extend API with additional endpoints
export const detailsApiEndpoints = detailsApi.injectEndpoints({
  endpoints: (builder) => ({
    getViolationFunction: builder.mutation<unknown, ViolationConflictApisProp>({
      query: ({ violationId, payload }) => ({
        url: `/api/sodms/sod-violations/${violationId}/functions`,
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetViolationFunctionApiResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        return [];
      },
    }),
    getViolationMitigatingControl: builder.mutation<
      unknown,
      { violationId: string }
    >({
      query: ({ violationId }) => ({
        url: `/api/sodms/sod-violations/${violationId}/mitigating-controls`,
        method: 'GET',
        isECMv6: true,
      }),
      invalidatesTags: ['EntitlementDetails'],
      transformResponse: (
        response: GetViolationMitigatingControlApiResponse
      ) => {
        if (response.status === 200) {
          return response.data;
        }

        return [];
      },
    }),
    getAssignedEntitlements: builder.mutation<
      unknown,
      ViolationConflictApisProp
    >({
      query: ({ violationId, functionId, payload }) => ({
        url: `/api/sodms/sod-violations/${violationId}/functions/${
          functionId ?? ''
        }/assigned-entitlements`,
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetAssignedEntitlementApiResponse) => {
        if (response.status === 200) {
          return { data: response.data, afterKey: response.afterKey };
        }

        return { data: [], afterKey: null };
      },
    }),
    getViolatingEntitlements: builder.mutation<
      unknown,
      ViolationConflictApisProp
    >({
      query: ({ violationId, functionId, assignedEntitlementId, payload }) => ({
        url: `/api/sodms/sod-violations/${violationId}/functions/${
          functionId ?? ''
        }/assigned-entitlements/${
          assignedEntitlementId ?? ''
        }/violating-entitlements`,
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetViolatingEntitlementApiResponse) => {
        if (response.status === 200) {
          return { data: response.data, searchAfter: response.searchAfter };
        }

        return { data: [], searchAfter: null };
      },
    }),
    getEntitlementDetails: builder.query<
      EntitlementDetailsRowType,
      { entitlementValueKey: number }
    >({
      query: ({ entitlementValueKey }) => ({
        url: `/api/sodms/inherent-risk/${entitlementValueKey}`,
        method: 'GET',
        isECMv6: true,
      }),
      providesTags: (_, __, arg) => [
        { type: 'EntitlementDetails', id: arg.entitlementValueKey },
      ],
      transformResponse: (response: {
        data: EntitlementDetailsRowType;
        status: number;
      }) => {
        if (response.status === 200) {
          return response.data;
        }

        throw new Error('Failed to fetch entitlement details');
      },
      onQueryStarted: (_, { dispatch }) => {
        dispatch(detailsActions.setEntitlementSummaryLoading(true));
      },
      onCacheEntryAdded: async (
        _,
        { dispatch, cacheDataLoaded, cacheEntryRemoved }
      ) => {
        try {
          const result = await cacheDataLoaded;

          dispatch(detailsActions.setEntitlementSummaryData(result.data));
        } catch (error) {
          dispatch(
            detailsActions.setEntitlementSummaryError(
              error instanceof Error ? error.message : 'Unknown error'
            )
          );
        }

        await cacheEntryRemoved;
      },
    }),
    getSodViolationsCountByStatus: builder.mutation<
      UserViolationChartDetails,
      { filters: SodViolationsCountFilters }
    >({
      query: ({ filters }) => ({
        url: '/api/sodms/sod-violations/count-by-status',
        method: 'POST',
        data: { filters },
        isECMv6: true,
      }),
      transformResponse: (response: GetSodViolationsCountApiResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        throw new Error(response.message || 'Failed to fetch violation counts');
      },
    }),
  }),
});

export const {
  useGetViolationFunctionMutation,
  useGetViolationMitigatingControlMutation,
  useGetAssignedEntitlementsMutation,
  useGetViolatingEntitlementsMutation,
  useGetEntitlementDetailsQuery,
  useGetSodViolationsCountByStatusMutation,
} = detailsApiEndpoints;

// Re-export the existing risks mutation
export { useGetInherentRisksMutation as useGetEntitlementRisksMutation };
